import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUp, FileText, Loader2, Mic, Paperclip, X } from 'lucide-react';
import VoiceRecordingBar from './VoiceRecordingBar';
import AIAgentModePicker from './AIAgentModePicker';
import { resolveAgentComposerPlaceholder } from '../config/agentModes';
import { getAgentPlaceholder } from '../hooks/useAIAgentChat';
import { DOCUMENT_ATTACHMENT_ACCEPT } from '../utils/documentUpload';

export default function AIAgentComposer({
  variant = 'compact',
  inputRef,
  inputMessage,
  setInputMessage,
  isProcessing,
  isReady,
  isVoiceSupported,
  isVoiceSession,
  isListening,
  isTranscribing,
  recordingMs,
  audioLevels,
  voiceError,
  onInputKeyDown,
  onFocus,
  onSend,
  onVoiceStart,
  onVoiceStop,
  onVoiceCancel,
  showShortcut = true,
  placeholder,
  lightSurface = false,
  contextCaption,
  agentMode,
  onAgentModeChange,
  pendingAttachment,
  attachmentError,
  onAttachmentSelect,
  onAttachmentClear,
  onAttachmentDrop,
}) {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.shared.aiAgent.composer.${key}`, opts);
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const isWorkspace = variant === 'workspace';
  const inputPlaceholder = resolveAgentComposerPlaceholder(
    agentMode,
    placeholder,
    getAgentPlaceholder(),
  );
  const canSend = Boolean(inputMessage.trim() || pendingAttachment) && !isProcessing;
  const thumbClass = isWorkspace
    ? lightSurface
      ? 'w-16 h-16 border-gray-200 bg-gray-50'
      : 'w-16 h-16 border-white/20 bg-white/5'
    : 'w-12 h-12 border-white/20 bg-white/5';

  const shellClass = [
    'relative',
    isWorkspace
      ? lightSurface
        ? 'rounded-2xl border border-gray-200 bg-white overflow-hidden'
        : 'rounded-2xl border border-white/20 bg-white/5 backdrop-blur-sm overflow-hidden'
      : '',
    isDragOver && !isVoiceSession
      ? lightSurface
        ? 'ring-2 ring-blue-400/60 ring-offset-2 ring-offset-white'
        : 'ring-2 ring-blue-400/50 ring-offset-2 ring-offset-slate-900/80'
      : '',
  ]
    .filter(Boolean)
    .join(' ');

  const textareaClass = isWorkspace
    ? lightSurface
      ? 'w-full min-h-[88px] max-h-[180px] resize-y border-none outline-none bg-transparent text-gray-900 px-4 pt-4 pb-1.5 font-inherit leading-normal placeholder:text-gray-400'
      : 'w-full min-h-[88px] max-h-[180px] resize-y border-none outline-none bg-transparent text-white px-4 pt-4 pb-1.5 font-inherit leading-normal placeholder:text-white/40'
    : 'w-full min-h-[28px] max-h-[120px] resize-none border-none bg-transparent text-slate-100 text-sm leading-snug outline-none px-1 py-0.5 pb-2 font-inherit placeholder:text-slate-400/55';

  const toolbarClass = isWorkspace
    ? lightSurface
      ? 'flex items-center justify-between gap-3 px-3 py-2 border-t border-gray-200 bg-gray-50/80'
      : 'flex items-center justify-between gap-3 px-3 py-2 border-t border-white/10 bg-white/[0.03]'
    : 'flex items-center justify-between gap-2 pt-0.5';

  const actionBtnClass = isWorkspace && lightSurface
    ? 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-800'
    : 'border-white/20 bg-white/5 text-slate-300 hover:bg-white/10 hover:text-slate-50';

  const handleDragEnter = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    if (isProcessing || isVoiceSession) return;
    setIsDragOver(true);
  }, [isProcessing, isVoiceSession]);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setIsDragOver(false);
  }, []);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(false);
      if (isProcessing || isVoiceSession || !onAttachmentDrop) return;
      onAttachmentDrop(event.dataTransfer?.files);
    },
    [isProcessing, isVoiceSession, onAttachmentDrop],
  );

  const handleFileInputChange = (event) => {
    const file = event.target.files?.[0];
    if (file && onAttachmentSelect) {
      onAttachmentSelect(file);
    }
    event.target.value = '';
  };

  return (
    <div
      className={shellClass}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={DOCUMENT_ATTACHMENT_ACCEPT}
        className="hidden"
        onChange={handleFileInputChange}
        aria-hidden
        tabIndex={-1}
      />

      {isDragOver && !isVoiceSession && (
        <div
          className={`pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-[inherit] border-2 border-dashed ${
            lightSurface
              ? 'border-blue-400/70 bg-blue-50/90 text-blue-700'
              : 'border-blue-400/60 bg-blue-500/10 text-blue-100'
          } ${isWorkspace ? '' : 'rounded-2xl'}`}
        >
          <p className="m-0 text-sm font-medium px-4 text-center">
            {p('drop_hint')}
          </p>
        </div>
      )}

      {pendingAttachment && !isVoiceSession && (
        <div
          className={`flex items-center gap-3 px-3 pt-3 ${
            isWorkspace ? 'px-4' : 'px-1'
          }`}
        >
          <div className="relative shrink-0">
            {pendingAttachment.kind === 'pdf' ? (
              <div
                className={`${thumbClass} rounded-lg border grid place-items-center ${
                  lightSurface ? 'text-red-600' : 'text-red-300'
                }`}
              >
                <FileText size={isWorkspace ? 28 : 22} />
              </div>
            ) : (
              <img
                src={pendingAttachment.previewUrl}
                alt={pendingAttachment.name || p('attached_document_alt')}
                className={`object-cover rounded-lg border ${thumbClass}`}
              />
            )}
            <button
              type="button"
              className={`absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full border grid place-items-center cursor-pointer ${
                lightSurface
                  ? 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                  : 'bg-slate-900 border-white/20 text-white/80 hover:bg-slate-800'
              }`}
              onClick={onAttachmentClear}
              disabled={isProcessing}
              aria-label={p('remove_attachment_aria')}
            >
              <X size={12} />
            </button>
          </div>
          <div className="min-w-0">
            <p
              className={`m-0 text-xs font-medium truncate ${
                lightSurface ? 'text-gray-800' : 'text-white/90'
              }`}
            >
              {pendingAttachment.name || p('document_fallback')}
            </p>
            <p className={`m-0 text-[0.68rem] ${lightSurface ? 'text-gray-500' : 'text-white/50'}`}>
              {pendingAttachment.kind === 'pdf'
                ? p('pdf_vision_hint')
                : p('image_vision_hint')}
            </p>
          </div>
        </div>
      )}

      {isVoiceSession ? (
        <VoiceRecordingBar
          isListening={isListening}
          isTranscribing={isTranscribing}
          recordingMs={recordingMs}
          audioLevels={audioLevels}
          onStop={onVoiceStop}
          onCancel={onVoiceCancel}
        />
      ) : (
        <textarea
          ref={inputRef}
          className={textareaClass}
          rows={isWorkspace ? 3 : 1}
          value={inputMessage}
          onChange={(e) => {
            setInputMessage(e.target.value);
            if (!isWorkspace) {
              e.target.style.height = 'auto';
              e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
            }
          }}
          onFocus={onFocus}
          onKeyDown={onInputKeyDown}
          placeholder={inputPlaceholder}
          disabled={isProcessing}
          aria-label={p('command_aria')}
        />
      )}

      <div className={toolbarClass}>
        <div className="flex items-center gap-2 min-w-0">
          {agentMode && onAgentModeChange ? (
            <AIAgentModePicker
              value={agentMode}
              onChange={onAgentModeChange}
              lightSurface={isWorkspace && lightSurface}
              disabled={isProcessing}
            />
          ) : null}
          {contextCaption ? (
            <span
              className={`text-[0.68rem] whitespace-nowrap truncate max-sm:hidden ${
                isWorkspace && lightSurface ? 'text-gray-500' : 'text-white/75'
              }`}
            >
              {contextCaption}
            </span>
          ) : null}
          {!isVoiceSession && showShortcut && !isWorkspace && (
            <span className="text-[0.62rem] text-white/50 px-1 py-0.5 rounded border border-white/20 max-sm:hidden">
              Ctrl+K
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          {!isVoiceSession && onAttachmentSelect && (
            <button
              type="button"
              className={`w-7 h-7 shrink-0 border rounded-lg cursor-pointer grid place-items-center transition-colors disabled:opacity-35 disabled:cursor-not-allowed ${actionBtnClass}`}
              onClick={() => fileInputRef.current?.click()}
              disabled={isProcessing || !isReady}
              aria-label={p('attach_aria')}
              title={p('attach_title')}
            >
              <Paperclip size={15} />
            </button>
          )}

          {!isVoiceSession && isReady && isVoiceSupported && (
            <button
              type="button"
              className={`w-7 h-7 shrink-0 border rounded-lg cursor-pointer grid place-items-center transition-colors disabled:opacity-35 disabled:cursor-not-allowed ${actionBtnClass}`}
              onClick={onVoiceStart}
              disabled={isProcessing}
              aria-label={p('speak_aria')}
              title={p('speak_title')}
            >
              <Mic size={15} />
            </button>
          )}

          {!isVoiceSession && (
            <button
              type="button"
              className={`w-[34px] h-[34px] shrink-0 border-none rounded-lg cursor-pointer grid place-items-center transition-all disabled:opacity-35 disabled:cursor-not-allowed disabled:hover:translate-y-0 ${
                isWorkspace
                  ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 hover:-translate-y-px shadow-lg shadow-blue-500/30'
                  : 'bg-slate-200 text-slate-900 hover:bg-white hover:-translate-y-px'
              }`}
              onClick={onSend}
              disabled={!canSend}
              aria-label={p('execute_aria')}
            >
              {isProcessing ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <ArrowUp size={16} />
              )}
            </button>
          )}
        </div>
      </div>

      {(voiceError || attachmentError) && !isVoiceSession && (
        <p
          className={`mt-1 px-3 pb-2 text-[0.68rem] ${
            isWorkspace && lightSurface ? 'text-red-600' : 'text-red-300'
          } ${isWorkspace ? '' : 'px-0.5'}`}
          role="status"
        >
          {attachmentError || voiceError}
        </p>
      )}

      {isWorkspace && !isVoiceSession && !pendingAttachment && (
        <p
          className={`px-4 pb-2 text-[0.68rem] ${
            lightSurface ? 'text-gray-400' : 'text-white/40'
          }`}
        >
          {p('drag_hint')}
        </p>
      )}
    </div>
  );
}
